import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import React from "react";
import { getSettings, updateCustomizations, updateSettings } from "src/api/settings";
import CustomCheckBox from "src/components/CustomCheckBox";
import CustomInput from "src/components/CustomInput";
import { Button } from "src/components/ui/button";
import FormInput from "src/components/widgets/FormInput";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import { cn, getCustomizationValue, showSuccessToast } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
	businessUuid: Yup.string().required(),
	business_name: Yup.string().required("This field is required"),
	business_address: Yup.string().required("This field is required"),
	vat: Yup.number().min(0, "Minimum amount is zero").max(100, "Maximum amount is 100").required("This field is required"),
	is_vat_enabled: Yup.boolean(),
	is_transaction_fee_enabled: Yup.boolean(),
});
const customizationValidationSchema = Yup.object().shape({
	businessUuid: Yup.string().required(),
	customizations: Yup.array().of(
		Yup.object().shape({
			label: Yup.string().required(),
			value: Yup.string().required("This field is required"),
		}),
	),
});

const Settings = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const { data } = useQuery({
		queryKey: ["settings"],
		queryFn: () => getSettings({ businessUuid: user?.business_uuid }),
		suspense: true,
	});

	const settings = data?.data;

	const initialValues = {
		businessUuid: user?.business_uuid,
		business_name: user.business_name || "",
		business_address: user.business_address || "",
		vat: settings?.vat ?? 2,
		is_vat_enabled: settings?.is_vat_enabled ?? true,
		is_transaction_fee_enabled: settings?.is_transaction_fee_enabled ?? true,
		customizations: user?.business_customization,
	};
	const customizationInitialValues = {
		businessUuid: user?.business_uuid,
		customizations: user?.business_customization,
	};

	const { mutate, isLoading } = useMutation(updateSettings, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["settings"]);
		},
	});
	const { mutate: customizationMutate, isLoading: isCustomizationLoading } = useMutation(updateCustomizations, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["user"]);
		},
	});

	return (
		<div className="pageWrapper">
			<div className="pageTop">
				<h2>Settings</h2>
			</div>

			<div className="innerContent tw-max-w-[800px] tw-mx-auto">
				<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
					{({ values, handleChange, handleSubmit, setFieldValue }) => (
						<form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-5">
							<FormInput
								className="formItem !tw-m-0 !tw-w-full"
								label="Business Name"
								name="business_name"
								type="text"
								placeholder="Business Name"
								value={values.business_name}
								onChange={handleChange}
							/>

							<FormInput
								className="formItem !tw-m-0 !tw-w-full"
								label="Business Address"
								name="business_address"
								type="text"
								placeholder="Business Address"
								value={values.business_address}
								onChange={handleChange}
							/>
							<CustomCheckBox
								id={`is_vat_enabled`}
								type="checkbox"
								className="tw-size-4 tw-border-border"
								label="Does your business charge VAT?"
								checked={values.is_vat_enabled}
								onChange={(event) => {
									const isChecked = event.target.checked;
									if (!isChecked) {
										setFieldValue("vat", 0);
									}
									handleChange(event);
								}}
							/>
							{values.is_vat_enabled && (
								<CustomInput name="vat" value={values.vat} onChange={handleChange} label="VAT (%)" placeholder="VAT" />
							)}
							<CustomCheckBox
								id={`is_transaction_fee_enabled`}
								type="checkbox"
								className="tw-size-4 tw-border-border"
								label={cn("Charge ", getCustomizationValue(user, "customer_label", true), " transaction fee?")}
								checked={values.is_transaction_fee_enabled}
								onChange={handleChange}
							/>
							<Button disabled={isLoading} className="tw-w-full tw-mt-auto tw-mb-3">
								Update Settings
							</Button>
						</form>
					)}
				</Formik>
			</div>

			<div className="pageTop tw-flex-col !tw-items-start !tw-gap-0 tw-mt-4">
				<h2>Labels</h2>
				<span className="tw-text-xs">Please ensure they are all in singular form (i.e Customer instead of customers)</span>
			</div>

			<div className="innerContent tw-max-w-[800px] tw-mx-auto">
				<Formik
					initialValues={customizationInitialValues}
					validationSchema={customizationValidationSchema}
					onSubmit={(values) => customizationMutate(values)}
				>
					{({ values, handleChange, handleSubmit }) => (
						<form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-5">
							<CustomInput
								label="Customer Label"
								name="customizations.0.value"
								type="text"
								placeholder="Customer Label"
								value={values.customizations[0].value}
								onChange={handleChange}
							/>
							<CustomInput
								label="Sub-Account Label"
								name="customizations.1.value"
								type="text"
								placeholder="Sub-Account Label"
								value={values.customizations[1].value}
								onChange={handleChange}
							/>
							<CustomInput
								label="Invoice Label"
								name="customizations.2.value"
								type="text"
								placeholder="Invoice Label"
								value={values.customizations[2].value}
								onChange={handleChange}
							/>
							<CustomInput
								label="Inventory Label"
								name="customizations.3.value"
								type="text"
								placeholder="Inventory Label"
								value={values.customizations[3].value}
								onChange={handleChange}
							/>
							<CustomInput
								label="Invoice Group Label"
								name="customizations.4.value"
								type="text"
								placeholder="Invoice Group Label"
								value={values.customizations[4].value}
								onChange={handleChange}
							/>

							<Button disabled={isCustomizationLoading} className="tw-w-full tw-mt-auto tw-mb-3">
								Update Customization
							</Button>
						</form>
					)}
				</Formik>
			</div>
		</div>
	);
};

const Comp = withPermissionWrapper(Settings, "update_business_settings");
export default Comp;
